import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  parse,
  setISOWeek,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
} from 'date-fns'
import type { WithFloatingProps } from 'react-datepicker/dist/with_floating'
import type { DatePickerType } from './datePicker.types'

export const isValidDate = (value: any) => {
  const date = new Date(value)
  const isValid = !Number.isNaN(date.getTime())
  return isValid
}

export const ensureDateWithinRange = (date: Date, minDate?: Date, maxDate?: Date): Date => {
  if (minDate && date < minDate) return minDate
  if (maxDate && date > maxDate) return maxDate
  return date
}

const getValidDate = (date?: Date | string | null): Date | undefined => {
  return date && isValidDate(date) ? new Date(date) : undefined
}

export const adjustDateByOneUnit = (date: Date, pickerType: DatePickerType, forward = true): Date => {
  const newDate = new Date(date)
  const direction = forward ? 1 : -1

  switch (pickerType) {
    case 'datetime':
      newDate.setMinutes(newDate.getMinutes() + 15 * direction)
      break
    case 'day':
      newDate.setDate(newDate.getDate() + 1 * direction)
      break
    case 'week':
      newDate.setDate(newDate.getDate() + 7 * direction)
      break
    case 'month':
      newDate.setMonth(newDate.getMonth() + 1 * direction)
      break
    case 'quarter':
      newDate.setMonth(newDate.getMonth() + 3 * direction)
      break
    case 'year':
      newDate.setFullYear(newDate.getFullYear() + 1 * direction)
      break
    default:
      newDate.setDate(newDate.getDate() + 1 * direction)
  }
  return newDate
}

export const getPossibleDate = (value?: string | Date | null, maxDate?: Date | string, minDate?: Date | string): Date | null => {
  if (value === null) return null

  const validatedMaxDate = maxDate && isValidDate(maxDate) ? new Date(maxDate) : undefined
  const validatedMinDate = minDate && isValidDate(minDate) ? new Date(minDate) : undefined
  const initialDate = value && isValidDate(value) ? new Date(value) : new Date()
  return ensureDateWithinRange(initialDate, validatedMinDate, validatedMaxDate)
}

// Basically what these functions do is determine a valid start date,
// defaulting to yesterday/today unless initial dates are provided and
// they fit within the range defined by minimum and maximum dates.
export const getStartDate = (
  pickerType: DatePickerType,
  dateRange?: [Date | string | null | undefined, Date | string | null | undefined],
  boundarydateRange?: [Date | string | undefined, Date | string | undefined],
  allowSameDateSelection?: boolean
): Date | null => {
  if (dateRange?.[0] === null) return null

  // 1. Validate all input dates
  const validDates = {
    min: getValidDate(boundarydateRange?.[0]),
    max: getValidDate(boundarydateRange?.[1]),
    start: getValidDate(dateRange?.[0]),
    end: getValidDate(dateRange?.[1]),
  }

  // 2. Get end date within range first (needed for comparison)
  const finalEndDate = validDates.end ? ensureDateWithinRange(validDates.end, validDates.min, validDates.max) : undefined

  // 3. Calculate and validate start date
  const defaultStartDate = subDays(new Date(), 1)
  let startDate = validDates.start ? validDates.start : defaultStartDate
  startDate = ensureDateWithinRange(startDate, validDates.min, validDates.max)

  // 4. Adjust start date if needed (when same date selection is not allowed)
  if (!allowSameDateSelection && finalEndDate && startDate >= finalEndDate) {
    startDate = adjustDateByOneUnit(finalEndDate, pickerType, false)
    startDate = ensureDateWithinRange(startDate, validDates.min, validDates.max)
  }

  return startDate
}

export const getEndDate = (
  pickerType: DatePickerType,
  dateRange?: [Date | string | null, Date | string | null],
  boundarydateRange?: [Date | string | undefined, Date | string | undefined],
  allowSameDateSelection = false
): Date | null => {
  // Early return if no end date provided
  if (dateRange?.[1] === null) return null

  // 1. Validate all input dates
  const validDates = {
    min: getValidDate(boundarydateRange?.[0]),
    max: getValidDate(boundarydateRange?.[1]),
    start: getValidDate(dateRange?.[0]),
    end: getValidDate(dateRange?.[1]),
  }

  // 2. Get start date within range first (needed for comparison)
  const finalStartDate = validDates.start ? ensureDateWithinRange(validDates.start, validDates.min, validDates.max) : validDates.min

  // 3. Calculate and validate end date
  let endDate = validDates.end ? validDates.end : new Date()
  endDate = ensureDateWithinRange(endDate, finalStartDate || validDates.min, validDates.max)

  // 4. Adjust end date if needed (when same date selection is not allowed)
  if (!allowSameDateSelection && finalStartDate && endDate <= finalStartDate) {
    endDate = adjustDateByOneUnit(finalStartDate, pickerType, true)
    // Ensure the adjusted date is still within range
    endDate = ensureDateWithinRange(endDate, finalStartDate || validDates.min, validDates.max)
  }

  return endDate
}

const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const buildWeekPrefixRegex = (weekPrefix: string) => new RegExp(`(?:${escapeRegex(weekPrefix)}|KW)\\s*(\\d{1,2})\\s+(\\d{4})`, 'i')

const getDateFnsFormat = (pickerType: DatePickerType): string => {
  switch (pickerType) {
    case 'month':
      return 'MM.yyyy'
    case 'quarter':
      return 'QQQ yyyy'
    case 'year':
      return 'yyyy'
    case 'datetime':
      return 'dd.MM.yyyy HH:mm'
    case 'week':
      // For weeks, we'll just parse the week number separately
      return 'w'
    case 'day':
      return 'dd.MM.yyyy'
    default:
      return 'dd.MM.yyyy'
  }
}

export const parseCustomDate = (dateString: string, pickerType: DatePickerType, weekPrefix = 'KW'): Date => {
  if (pickerType === 'week') {
    const match = dateString.match(buildWeekPrefixRegex(weekPrefix))
    if (match) {
      const weekNumber = Number.parseInt(match[1])
      const year = Number.parseInt(match[2])
      // Create a date from the week number
      const date = new Date(year, 0, 1 + (weekNumber - 1) * 7)
      return startOfWeek(date, { weekStartsOn: 1 })
    }
    return new Date()
  }

  const format = getDateFnsFormat(pickerType)
  return parse(dateString, format, new Date())
}

export const getPickerTypeProps = (pickerType: DatePickerType, weekPrefix = 'KW') => {
  switch (pickerType) {
    case 'month':
      return { showMonthYearPicker: true, dateFormat: 'MM.yyyy' }
    case 'quarter':
      return { showQuarterYearPicker: true, dateFormat: 'QQQ yyyy' }
    case 'year':
      return { showYearPicker: true, dateFormat: 'yyyy', showTimeSelect: false }
    case 'datetime':
      return { showTimeSelect: false, dateFormat: 'dd.MM.yyyy HH:mm', timeFormat: 'HH:mm' }
    case 'week':
      return { dateFormat: `'${weekPrefix}'ww YYYY`, showWeekNumbers: true }
    case 'day':
      return { dateFormat: 'dd.MM.yyyy' }
    default:
      return { dateFormat: 'dd.MM.yyyy' }
  }
}

export const offsetDateBasedOnPickerType = (pickerType: DatePickerType, date: Date, type: 'start' | 'end' = 'start') => {
  switch (pickerType) {
    case 'day':
      if (type === 'start') return startOfDay(date)
      return endOfDay(date)
    case 'week':
      if (type === 'start') return startOfWeek(date, { weekStartsOn: 1 })
      return endOfWeek(date, { weekStartsOn: 1 })
    case 'month':
      if (type === 'start') return startOfMonth(date)
      return endOfMonth(date)
    case 'quarter':
      if (type === 'start') return startOfQuarter(date)
      return endOfQuarter(date)
    case 'year':
      if (type === 'start') return startOfYear(date)
      return endOfYear(date)
    case 'datetime':
      return roundToNearestQuarterHour(date)
    default:
      return date
  }
}

export const getPopperModifiers = (): WithFloatingProps['popperModifiers'] => [
  {
    name: 'offset',
    options: {
      offset: args => {
        if (args.placement === 'top') return [0, -15]
        return [0, 0]
      },
    },
    fn(state) {
      return state
    },
  },
  {
    name: 'preventOverflow',
    options: { rootBoundary: 'viewport', tether: false, altAxis: true },
    fn(state) {
      return state
    },
  },
]

interface ParseManualInputResult {
  dateRange: [Date | null, Date | null]
  rawInput: string
}

export const parseManualInput = (
  value: string,
  pickerType: DatePickerType,
  isRange: boolean,
  currentDates: [Date | null, Date | null],
  minDate?: Date,
  maxDate?: Date,
  weekPrefix = 'KW'
): ParseManualInputResult => {
  // Default return with current dates
  const defaultResult = {
    dateRange: currentDates,
    rawInput: value,
  }

  try {
    if (isRange) {
      const [startStr, endStr] = value.split('-').map(s => s.trim())
      if (!startStr || !endStr) return defaultResult

      let parsedStart: Date | null = null
      let parsedEnd: Date | null = null

      switch (pickerType) {
        case 'week': {
          const weekPrefixRegex = buildWeekPrefixRegex(weekPrefix)
          const startMatch = startStr.match(weekPrefixRegex)
          const endMatch = endStr.match(weekPrefixRegex)
          if (startMatch && endMatch) {
            const startYear = Number.parseInt(startMatch[2])
            const startWeek = Number.parseInt(startMatch[1])
            const endYear = Number.parseInt(endMatch[2])
            const endWeek = Number.parseInt(endMatch[1])

            // Ensure the correct year is used for the ISO week
            parsedStart = startOfISOWeek(setISOWeek(new Date(startYear, 0, 4), startWeek))
            parsedEnd = startOfISOWeek(setISOWeek(new Date(endYear, 0, 4), endWeek))
          }
          break
        }
        case 'month': {
          // Format: "01.2024 - 10.2024"
          const startMatch = startStr.match(/(\d{1,2})\.?(\d{4})/)
          const endMatch = endStr.match(/(\d{1,2})\.?(\d{4})/)
          if (startMatch && endMatch) {
            parsedStart = new Date(Number.parseInt(startMatch[2]), Number.parseInt(startMatch[1]) - 1)
            parsedEnd = new Date(Number.parseInt(endMatch[2]), Number.parseInt(endMatch[1]) - 1)
          }
          break
        }
        case 'datetime': {
          // Format: "21.10.2024 08:00 - 21.10.2024 09:00"
          const startMatch = startStr.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})/)
          const endMatch = endStr.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})/)
          if (startMatch && endMatch) {
            parsedStart = new Date(
              Number.parseInt(startMatch[3]),
              Number.parseInt(startMatch[2]) - 1,
              Number.parseInt(startMatch[1]),
              Number.parseInt(startMatch[4]),
              Number.parseInt(startMatch[5])
            )
            parsedEnd = new Date(
              Number.parseInt(endMatch[3]),
              Number.parseInt(endMatch[2]) - 1,
              Number.parseInt(endMatch[1]),
              Number.parseInt(endMatch[4]),
              Number.parseInt(endMatch[5])
            )

            // Round to the nearest 15-minute interval
            parsedStart = roundToNearestQuarterHour(parsedStart)
            parsedEnd = roundToNearestQuarterHour(parsedEnd)
          }
          break
        }
        case 'year': {
          // Format: "2023 - 2024"
          const startYear = Number.parseInt(startStr)
          const endYear = Number.parseInt(endStr)
          if (!Number.isNaN(startYear) && !Number.isNaN(endYear)) {
            parsedStart = new Date(startYear, 0, 1)
            parsedEnd = new Date(endYear, 11, 31)
          }
          break
        }
        case 'quarter': {
          // Format: "Q1 2024 - Q4 2024"
          const startMatch = startStr.match(/Q(\d)\s+(\d{4})/)
          const endMatch = endStr.match(/Q(\d)\s+(\d{4})/)
          if (startMatch && endMatch) {
            parsedStart = new Date(Number.parseInt(startMatch[2]), (Number.parseInt(startMatch[1]) - 1) * 3, 1)
            parsedEnd = new Date(Number.parseInt(endMatch[2]), (Number.parseInt(endMatch[1]) - 1) * 3 + 2, 1)
            parsedEnd = new Date(parsedEnd.getFullYear(), parsedEnd.getMonth() + 1, 0) // Last day of the quarter
          }
          break
        }
        default: {
          // Format: "01.04.2024 - 23.10.2024"
          const startMatch = startStr.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/)
          const endMatch = endStr.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/)
          if (startMatch && endMatch) {
            parsedStart = new Date(Number.parseInt(startMatch[3]), Number.parseInt(startMatch[2]) - 1, Number.parseInt(startMatch[1]))
            parsedEnd = new Date(Number.parseInt(endMatch[3]), Number.parseInt(endMatch[2]) - 1, Number.parseInt(endMatch[1]))
          }
        }
      }

      // Validate dates
      if (parsedStart && parsedEnd) {
        const validStart = getStartDate(pickerType, [parsedStart, parsedEnd], [minDate, maxDate])
        const validEnd = getEndDate(pickerType, [validStart, parsedEnd], [minDate, maxDate])

        return {
          dateRange: [validStart, validEnd],
          rawInput: value,
        }
      }
    } else {
      // Single date parsing
      let parsedDate: Date | null = null

      switch (pickerType) {
        case 'week': {
          const match = value.match(buildWeekPrefixRegex(weekPrefix))
          if (match) {
            const year = Number.parseInt(match[2])
            const week = Number.parseInt(match[1])
            parsedDate = startOfISOWeek(setISOWeek(new Date(year, 0, 4), week))
          }
          break
        }
        case 'month': {
          // Format: "01.2024"
          const match = value.match(/(\d{1,2})\.?(\d{4})/)
          if (match) {
            parsedDate = new Date(Number.parseInt(match[2]), Number.parseInt(match[1]) - 1)
          }
          break
        }
        case 'datetime': {
          // Format: "21.10.2024 08:00"
          const match = value.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{2})/)
          if (match) {
            parsedDate = new Date(
              Number.parseInt(match[3]),
              Number.parseInt(match[2]) - 1,
              Number.parseInt(match[1]),
              Number.parseInt(match[4]),
              Number.parseInt(match[5])
            )

            // Round to the nearest 15-minute interval
            parsedDate = roundToNearestQuarterHour(parsedDate)
          }
          break
        }
        case 'year': {
          // Format: "2024"
          const year = Number.parseInt(value)
          if (!Number.isNaN(year)) {
            parsedDate = new Date(year, 0, 1)
          }
          break
        }
        case 'quarter': {
          // Format: "Q1 2024"
          const match = value.match(/Q(\d)\s+(\d{4})/)
          if (match) {
            parsedDate = new Date(Number.parseInt(match[2]), (Number.parseInt(match[1]) - 1) * 3, 1)
          }
          break
        }
        default: {
          // Format: "01.04.2024"
          const match = value.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/)
          if (match) {
            parsedDate = new Date(Number.parseInt(match[3]), Number.parseInt(match[2]) - 1, Number.parseInt(match[1]))
          }
        }
      }

      // Validate single date
      if (parsedDate) {
        const validDate = getPossibleDate(parsedDate, maxDate, minDate)
        return {
          dateRange: [validDate, null],
          rawInput: value,
        }
      }
    }

    return defaultResult
  } catch (error) {
    console.error('Error parsing manual input:', error)
    return defaultResult
  }
}

// Helper function to round a date to the nearest 15-minute interval
function roundToNearestQuarterHour(date: Date): Date {
  const minutes = date.getMinutes()
  const roundedMinutes = Math.round(minutes / 15) * 15
  date.setMinutes(roundedMinutes)
  date.setSeconds(0)
  date.setMilliseconds(0)
  return date
}
